import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Search, Plus, Trash2, Loader2, FileDown, Download, CalendarDays, Target,
  Award, Clock, Sparkles, Wand2, X, ChevronRight,
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import api from "@/lib/api";
import { Head, Loader, EmptyBox } from "./shared";

const INPUT = "w-full bg-navy border border-white/15 text-white px-3 py-2 text-sm focus:border-gold focus:outline-none";
const STATUSES = ["Saved", "Applied", "Awarded", "Declined"];
const statusColor = (s) => s === "Awarded" ? "text-green-400" : s === "Applied" ? "text-gold" : s === "Declined" ? "text-red-400" : "text-gray-400";

// --- tiny markdown renderer (headings, bold, lists, paragraphs) ---
function Markdown({ text }) {
  const lines = (text || "").split("\n");
  const out = [];
  let list = [];
  const flush = (i) => { if (list.length) { out.push(<ul key={`u${i}`} className="list-disc ml-5 space-y-1 my-2 text-gray-300 text-sm">{list}</ul>); list = []; } };
  const inline = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, j) => p.startsWith("**") && p.endsWith("**")
    ? <strong key={j} className="text-white">{p.slice(2, -2)}</strong> : p);
  lines.forEach((ln, i) => {
    const t = ln.trim();
    if (t.startsWith("## ")) { flush(i); out.push(<h3 key={i} className="text-gold font-semibold text-sm uppercase tracking-wider mt-4 mb-1">{t.slice(3)}</h3>); }
    else if (t.startsWith("### ")) { flush(i); out.push(<h4 key={i} className="text-white font-semibold text-sm mt-3 mb-1">{t.slice(4)}</h4>); }
    else if (t.startsWith("- ") || t.startsWith("* ")) { list.push(<li key={i}>{inline(t.slice(2))}</li>); }
    else if (/^\d+\.\s/.test(t)) { list.push(<li key={i} className="list-decimal">{inline(t.replace(/^\d+\.\s/, ""))}</li>); }
    else if (t === "") { flush(i); }
    else { flush(i); out.push(<p key={i} className="text-gray-300 text-sm my-1.5">{inline(t)}</p>); }
  });
  flush("end");
  return <div>{out}</div>;
}

export function GrantFinder() {
  const [prospects, setProspects] = useState(null);
  const [selId, setSelId] = useState("");
  const [prospect, setProspect] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  const load = () => api.get("/admin/grant-finder/prospects").then((r) => setProspects(r.data.prospects || [])).catch(() => setProspects([]));
  useEffect(() => { load(); }, []);

  const select = async (id) => {
    setSelId(id);
    setProspect(null);
    try { const { data } = await api.get(`/admin/grant-finder/prospects/${id}`); setProspect(data.prospect); }
    catch (e) { toast.error("Could not load prospect"); }
  };

  const create = async () => {
    if (!newName.trim()) { toast.error("Enter a business/prospect name"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/grant-finder/prospects", { name: newName.trim() });
      setNewName("");
      await load();
      select(data.prospect.id);
      toast.success("Prospect created — add details and find grants.");
    } catch (e) { toast.error("Could not create prospect"); } finally { setCreating(false); }
  };

  const removeProspect = async (id) => {
    try { await api.delete(`/admin/grant-finder/prospects/${id}`); if (selId === id) { setSelId(""); setProspect(null); } load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  if (prospects === null) return <Loader />;

  return (
    <div data-testid="admin-grant-finder">
      <Head title="Grant Finder" desc="Your private grant-finding workbench. Research grants for any business — yours or a prospect's — organize leads into a pipeline, export to share, and get an AI Grant Strategist playbook that walks a beginner through applying. Wear the grant-finder hat and offer it as a service." />

      <div className="grid lg:grid-cols-[300px_1fr] gap-5">
        {/* Prospect list */}
        <div className="space-y-3">
          <div className="bg-navy-800 border border-white/10 p-3">
            <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">New prospect</div>
            <div className="flex gap-2">
              <input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()}
                placeholder="Business / prospect name" className={INPUT} data-testid="gf-new-name" />
              <button onClick={create} disabled={creating} data-testid="gf-create"
                className="inline-flex items-center gap-1 bg-gold text-navy px-3 text-sm font-semibold hover:bg-gold-light disabled:opacity-60">
                {creating ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5" data-testid="gf-prospect-list">
            {prospects.length === 0 && <EmptyBox text="No prospects yet. Add one above to start." />}
            {prospects.map((p) => (
              <button key={p.id} onClick={() => select(p.id)} data-testid={`gf-prospect-${p.id}`}
                className={`w-full text-left p-3 border transition-colors ${selId === p.id ? "border-gold/60 bg-gold/5" : "border-white/10 bg-navy-800 hover:border-white/25"}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-white truncate">{p.name}</span>
                  <ChevronRight size={14} className="text-gray-500 shrink-0" />
                </div>
                <div className="text-[11px] text-gray-500 mt-0.5">{p.industry || "—"}{p.location ? ` · ${p.location}` : ""} · {p.grant_count || 0} grant{(p.grant_count || 0) === 1 ? "" : "s"}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Workbench */}
        <div>
          {!selId && <EmptyBox text="Select a prospect on the left, or create one, to research grants." />}
          {selId && !prospect && <Loader />}
          {prospect && <Workbench key={prospect.id} prospect={prospect} onDelete={removeProspect} onRenamed={load} />}
        </div>
      </div>
    </div>
  );
}

function Workbench({ prospect, onDelete, onRenamed }) {
  const base = `/admin/grant-finder/prospects/${prospect.id}`;
  const [form, setForm] = useState({
    name: prospect.name || "", industry: prospect.industry || "", location: prospect.location || "",
    stage: prospect.stage || "", amount_needed: prospect.amount_needed || "", purpose: prospect.purpose || "", notes: prospect.notes || "",
  });
  const [savingIntake, setSavingIntake] = useState(false);
  const [finding, setFinding] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [picked, setPicked] = useState({});
  const [pipeline, setPipeline] = useState(prospect.grants || []);
  const [amtDraft, setAmtDraft] = useState({});
  const [goal, setGoal] = useState(prospect.funding_goal || 0);
  const [goalInput, setGoalInput] = useState(prospect.funding_goal ? String(prospect.funding_goal) : "");
  const [savingGoal, setSavingGoal] = useState(false);
  const [showCal, setShowCal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [strat, setStrat] = useState(null); // {name, loading, content}

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveIntake = async () => {
    setSavingIntake(true);
    try { await api.put(base, form); toast.success("Prospect details saved."); onRenamed?.(); }
    catch (e) { toast.error("Could not save"); } finally { setSavingIntake(false); }
  };

  const findGrants = async () => {
    setFinding(true); setSuggestions([]);
    try {
      const { data } = await api.post(`${base}/find`);
      setSuggestions(data.grants || []);
      if (!data.grants?.length) toast.error("No grant suggestions came back — try adding more detail.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Grant search failed"); } finally { setFinding(false); }
  };

  const persist = async (list) => { await api.put(`${base}/grants`, { grants: list }); setPipeline(list); };
  const addPicked = async () => {
    const chosen = suggestions.filter((_, i) => picked[i]);
    const merged = [...pipeline, ...chosen.filter((c) => !pipeline.some((p) => p.name === c.name))];
    try { await persist(merged); setPicked({}); toast.success("Added to pipeline."); } catch (e) { toast.error("Could not save"); }
  };
  const setStatus = async (i, status) => {
    const list = pipeline.map((g, idx) => idx === i ? { ...g, status } : g);
    try { await persist(list); if (status === "Awarded") toast.success("🎉 Marked Awarded!"); } catch (e) { toast.error("Could not update"); }
  };
  const removeGrant = async (i) => { try { await persist(pipeline.filter((_, idx) => idx !== i)); } catch (e) { toast.error("Could not remove"); } };
  const amtValue = (i, g) => amtDraft[i] !== undefined ? amtDraft[i] : (g.amount ? String(g.amount) : "");
  const saveAmount = async (i) => {
    if (amtDraft[i] === undefined) return;
    const amount = Math.max(0, Number(amtDraft[i]) || 0);
    const list = pipeline.map((g, idx) => idx === i ? { ...g, amount } : g);
    try { await persist(list); } catch (e) { toast.error("Could not save amount"); }
    finally { setAmtDraft((d) => { const n = { ...d }; delete n[i]; return n; }); }
  };
  const saveGoal = async () => {
    setSavingGoal(true);
    try { const { data } = await api.put(`${base}/funding-goal`, { goal: Number(goalInput) || 0 }); setGoal(data.funding_goal); toast.success("Target saved."); }
    catch (e) { toast.error("Could not save target"); } finally { setSavingGoal(false); }
  };
  const exportCsv = () => {
    const rows = [["Grant", "Provider", "Status", "Amount", "Window"], ...pipeline.map((g) => [g.name || "", g.provider || "", g.status || "Saved", g.amount || 0, g.deadline || ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "grant-pipeline.csv"; a.click(); URL.revokeObjectURL(url);
  };
  const exportPdf = async () => {
    setExporting(true);
    try { const res = await api.get(`${base}/export.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "grant-pipeline.pdf"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not export PDF"); } finally { setExporting(false); }
  };
  const runStrategist = async (g) => {
    setStrat({ name: g.name, loading: true, content: g.strategy || "" });
    try { const { data } = await api.post(`${base}/strategist`, { grant: g });
      setStrat({ name: g.name, loading: false, content: data.strategy || "" });
      // reflect saved strategy locally
      setPipeline((list) => list.map((x) => x.name === g.name ? { ...x, strategy: data.strategy } : x));
    } catch (e) { toast.error(e?.response?.data?.detail || "Strategist failed"); setStrat(null); }
  };

  const parseDate = (s) => { if (!s) return null; const d = new Date(s); return isNaN(d.getTime()) ? null : d; };
  const closingSoon = (g) => { if (g.status === "Awarded" || g.status === "Declined") return false; const d = parseDate(g.deadline); if (!d) return false; const days = (d - Date.now()) / 86400000; return days >= 0 && days <= 30; };
  const upcoming = pipeline.map((g) => ({ ...g, _d: parseDate(g.deadline) })).filter((g) => g._d && g.status !== "Awarded" && g.status !== "Declined").sort((a, b) => a._d - b._d);
  const deadlineDates = upcoming.map((g) => g._d);
  const awarded = pipeline.filter((g) => g.status === "Awarded");
  const totalSecured = awarded.reduce((s, g) => s + (Number(g.amount) || 0), 0);

  const F = ({ label, k, ph, area }) => (
    <div>
      <label className="text-xs text-gray-400 mb-1 block">{label}</label>
      {area
        ? <textarea value={form[k]} onChange={(e) => set(k, e.target.value)} rows={2} className={INPUT + " resize-y"} placeholder={ph} data-testid={`gf-intake-${k}`} />
        : <input value={form[k]} onChange={(e) => set(k, e.target.value)} className={INPUT} placeholder={ph} data-testid={`gf-intake-${k}`} />}
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Intake */}
      <div className="bg-navy-800 border border-white/10 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm text-white font-semibold">Prospect details</div>
          <button onClick={() => onDelete(prospect.id)} data-testid="gf-delete" className="text-gray-500 hover:text-red-400 inline-flex items-center gap-1 text-xs"><Trash2 size={13} /> Delete</button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <F label="Business / prospect name" k="name" ph="e.g. Luverne Bakery" />
          <F label="Industry" k="industry" ph="e.g. Bakery / Food service" />
          <F label="Location / state" k="location" ph="e.g. Alabama" />
          <F label="Business stage" k="stage" ph="e.g. Startup, Growing, Established" />
          <F label="Amount needed" k="amount_needed" ph="e.g. $50,000" />
          <F label="What the funding is for" k="purpose" ph="e.g. Equipment, hiring, expansion" />
        </div>
        <div className="mt-3"><F label="Notes" k="notes" ph="Anything else that helps target grants…" area /></div>
        <div className="flex items-center gap-2 mt-3">
          <button onClick={saveIntake} disabled={savingIntake} data-testid="gf-save-intake" className="text-sm border border-white/15 text-white px-4 py-2 hover:border-gold/50 disabled:opacity-60">{savingIntake ? "Saving…" : "Save details"}</button>
          <button onClick={findGrants} disabled={finding} data-testid="gf-find" className="inline-flex items-center gap-2 bg-gold text-navy px-4 py-2 text-sm font-semibold hover:bg-gold-light disabled:opacity-60">
            {finding ? <Loader2 className="animate-spin" size={15} /> : <Search size={15} />} Find grants
          </button>
        </div>
      </div>

      {/* Suggestions */}
      {(finding || suggestions.length > 0) && (
        <div className="bg-navy-800 border border-white/10 p-4" data-testid="gf-suggestions">
          <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">AI grant leads</div>
          {finding && <div className="flex items-center gap-2 text-sm text-gray-300 py-6 justify-center"><Loader2 className="animate-spin" size={16} /> Researching grants…</div>}
          {!finding && suggestions.map((g, i) => (
            <label key={i} data-testid={`gf-suggestion-${i}`} className={`block p-3 mb-2 border cursor-pointer ${picked[i] ? "border-gold/60" : "border-white/10 hover:border-white/25"}`}>
              <div className="flex items-start gap-3">
                <input type="checkbox" checked={!!picked[i]} onChange={() => setPicked((s) => ({ ...s, [i]: !s[i] }))} className="mt-1 accent-gold w-4 h-4" data-testid={`gf-suggestion-check-${i}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white">{g.name}</div>
                  {g.provider && <div className="text-xs text-gold">{g.provider}</div>}
                  {g.why_fit && <p className="text-xs text-gray-300 mt-1"><span className="text-gray-500">Why it fits:</span> {g.why_fit}</p>}
                  {g.deadline && <p className="text-xs text-gray-300 mt-1"><span className="text-gray-500">Window:</span> <span className="text-gold">{g.deadline}</span></p>}
                </div>
              </div>
            </label>
          ))}
          {!finding && suggestions.length > 0 && (
            <button onClick={addPicked} disabled={!Object.values(picked).some(Boolean)} data-testid="gf-add-picked" className="mt-2 inline-flex items-center gap-2 bg-gold/15 text-gold border border-gold/30 px-4 py-2 text-sm hover:bg-gold/25 disabled:opacity-50">
              <Plus size={14} /> Add selected to pipeline
            </button>
          )}
        </div>
      )}

      {/* Pipeline */}
      {pipeline.length > 0 && (
        <div className="bg-navy-800 border border-white/10 p-4" data-testid="gf-pipeline">
          {/* Funding progress */}
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="flex items-center gap-2 text-sm text-white font-semibold"><Target size={15} className="text-gold" /> Funding pipeline</div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-400">Target $</span>
              <input type="number" min="0" value={goalInput} onChange={(e) => setGoalInput(e.target.value)} data-testid="gf-goal-input" className="w-28 bg-navy border border-white/15 text-white text-xs px-2 py-1 focus:border-gold focus:outline-none" placeholder="0" />
              <button onClick={saveGoal} disabled={savingGoal} data-testid="gf-goal-save" className="text-xs bg-gold/15 text-gold border border-gold/30 px-2.5 py-1 hover:bg-gold/25 disabled:opacity-60">{savingGoal ? "…" : "Set"}</button>
            </div>
          </div>
          {goal > 0 && (
            <>
              <div className="h-3 bg-navy border border-white/10 overflow-hidden"><div className="h-full bg-green-500 transition-all" data-testid="gf-funding-bar" style={{ width: `${Math.min(100, Math.round(totalSecured / goal * 100))}%` }} /></div>
              <div className="flex justify-between text-xs mt-1.5"><span className="text-green-400">${totalSecured.toLocaleString()} secured</span><span className="text-gray-400">{Math.round(totalSecured / goal * 100)}% of ${goal.toLocaleString()}</span></div>
            </>
          )}
          {awarded.length > 0 && <div className="text-sm text-green-300 mt-2 flex items-center gap-1.5" data-testid="gf-secured-tally"><Award size={14} />{totalSecured > 0 ? `Funded! $${totalSecured.toLocaleString()} secured across ${awarded.length} grant${awarded.length > 1 ? "s" : ""}` : `Funded! ${awarded.length} grant${awarded.length > 1 ? "s" : ""} secured`}</div>}

          {/* Toolbar */}
          <div className="flex items-center justify-end gap-3 mt-3 mb-2">
            <button onClick={() => setShowCal((v) => !v)} data-testid="gf-calendar-toggle" className="inline-flex items-center gap-1 text-xs text-gray-300 hover:text-gold"><CalendarDays size={13} /> Calendar</button>
            <button onClick={exportCsv} data-testid="gf-export-csv" className="inline-flex items-center gap-1 text-xs text-gray-300 hover:text-gold"><Download size={13} /> CSV</button>
            <button onClick={exportPdf} disabled={exporting} data-testid="gf-export-pdf" className="inline-flex items-center gap-1 text-xs text-gray-300 hover:text-gold disabled:opacity-50">{exporting ? <Loader2 className="animate-spin" size={13} /> : <FileDown size={13} />} PDF</button>
          </div>
          {showCal && (
            <div className="mb-3 bg-navy border border-white/10 p-3" data-testid="gf-calendar">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="text-white"><Calendar mode="single" defaultMonth={deadlineDates[0] || new Date()} modifiers={{ deadline: deadlineDates }} modifiersClassNames={{ deadline: "bg-gold/30 text-gold font-bold rounded-md" }} /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Upcoming windows</div>
                  {upcoming.length === 0 && <p className="text-xs text-gray-500">No dated deadlines. Add a date-style window (e.g. "Sep 15 2026").</p>}
                  {upcoming.map((g, i) => { const days = Math.ceil((g._d - Date.now()) / 86400000); return (
                    <div key={i} className="flex items-center justify-between gap-2 text-xs mb-1" data-testid={`gf-cal-upcoming-${i}`}><span className="text-gray-300 truncate">{g.name}</span><span className={days <= 30 ? "text-amber-300 shrink-0" : "text-gray-500 shrink-0"}>{g._d.toLocaleDateString()} · {days >= 0 ? `${days}d` : "passed"}</span></div>); })}
                </div>
              </div>
            </div>
          )}

          {/* Rows */}
          <div className="space-y-2">
            {pipeline.map((g, i) => (
              <div key={i} className="bg-navy border border-white/10 p-3" data-testid={`gf-grant-${i}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-white truncate">{g.name}</span>
                      {g.status === "Awarded" && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-300 bg-green-500/15 border border-green-500/30 px-1.5 py-0.5 shrink-0"><Award size={11} /> Funded!</span>}
                      {closingSoon(g) && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-300 bg-amber-500/15 border border-amber-500/30 px-1.5 py-0.5 shrink-0" data-testid={`gf-closing-${i}`}><Clock size={11} /> Closing soon</span>}
                    </div>
                    {g.provider && <div className="text-[11px] text-gray-500">{g.provider}{g.deadline ? ` · ${g.deadline}` : ""}</div>}
                    {g.status === "Awarded" && (
                      <div className="flex items-center gap-1.5 mt-1.5"><span className="text-[11px] text-gray-500">Amount secured $</span>
                        <input type="number" min="0" value={amtValue(i, g)} data-testid={`gf-amount-${i}`} onChange={(e) => setAmtDraft((d) => ({ ...d, [i]: e.target.value }))} onBlur={() => saveAmount(i)} className="w-28 bg-navy border border-white/15 text-xs text-green-300 px-2 py-1 focus:border-gold focus:outline-none" placeholder="0" /></div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => runStrategist(g)} data-testid={`gf-strategist-${i}`} className="inline-flex items-center gap-1 text-xs text-gold border border-gold/30 px-2 py-1 hover:bg-gold/10"><Wand2 size={12} /> Strategist</button>
                    <select value={g.status || "Saved"} onChange={(e) => setStatus(i, e.target.value)} data-testid={`gf-status-${i}`} className={`bg-navy border border-white/15 text-xs px-2 py-1 focus:border-gold focus:outline-none ${statusColor(g.status)}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button onClick={() => removeGrant(i)} data-testid={`gf-remove-${i}`} className="text-gray-500 hover:text-red-400"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Strategist modal */}
      {strat && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="gf-strategist-modal" onClick={() => setStrat(null)}>
          <div className="bg-navy border border-white/15 w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10 sticky top-0 bg-navy">
              <div className="flex items-center gap-2 text-white font-semibold"><Sparkles size={16} className="text-gold" /> Grant Strategist — {strat.name}</div>
              <button onClick={() => setStrat(null)} data-testid="gf-strategist-close" className="text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-5">
              {strat.loading && <div className="flex items-center gap-2 text-sm text-gray-300 py-8 justify-center"><Loader2 className="animate-spin" size={16} /> Building your beginner-friendly application playbook…</div>}
              {!strat.loading && <Markdown text={strat.content} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
